/**
 * The ten homepage FAQ items, in the order they are rendered.
 *
 * Single source of truth: `FaqSection` in HomePage.tsx renders these and the
 * homepage FAQPage JSON-LD is generated from the same array, so the visible
 * copy and the structured data cannot drift apart. `link` is an inline link
 * rendered at the end of the answer; its label is part of the answer text for
 * schema purposes and carries no URL there.
 */
import { TRIAL_CONTACTS_PHRASE } from "@/lib/trial";
export type HomeFaqItem = {
  question: string;
  answer: string;
  link?: { to: "/compare" | "/connect"; label: string };
};

export const HOME_FAQ_ITEMS: readonly HomeFaqItem[] = [
  {
    question: "What is CapitalDart?",
    answer:
      "CapitalDart is an AI capital raising platform for companies, real estate sponsors, fund managers and borrowers. It combines a directory of 70,000+ vetted investors with contact-level matching, diligence reports, outreach and deal pipeline tracking. Use the platform yourself with AI-assisted tasks, or choose AgentDart to work toward your deal goals and save emails to drafts or send them with permission.",
  },
  {
    question: "What do I get in the free trial?",
    answer:
      `The free trial includes ${TRIAL_CONTACTS_PHRASE}, personalized outreach drafts and an Investor Readiness Report. Create an account and provide your deal materials to try the output on your own raise. No credit card is required.`,
  },
  {
    question: "How does CapitalDart find the right investors?",
    answer:
      "CapitalDart compares your deal with investor criteria such as industry, stage, geography, investment size and recent activity. It identifies the relevant contact at each firm and provides a fit score with supporting rationale.",
  },
  {
    question: "How is it different from an investor database?",
    answer:
      "CapitalDart connects investor research to the work that follows: finding the relevant person, reviewing your materials, preparing outreach and tracking conversations. You can use the platform to run your own process or support work with an adviser.",
    link: { to: "/compare", label: "Compare the approaches." },
  },
  {
    question: "Can I find acquirers, LPs and lenders too?",
    answer:
      "Yes. CapitalDart supports companies raising equity or selling, real estate sponsors, fund managers seeking LP capital and borrowers seeking debt. Matching adapts to the relevant investor or buyer universe.",
  },
  {
    question: "Do I have to use AgentDart?",
    answer:
      "No. The built-in chat and the platform cover the whole raise on their own: matching, contacts, outreach drafts, reports and pipeline tracking. AgentDart is an option for when you want to hand off execution toward a goal, and you can start it, pause it or turn it off from the same chat at any time.",
  },
  {
    question: "What can AgentDart do for my capital raise?",
    answer:
      "AgentDart helps run your capital-raising workflow around a deal goal. It can execute a 30-day plan, work toward a funnel target, find warm introduction paths, support a LinkedIn post strategy and prepare outreach and follow-ups. You can also set a custom goal.",
  },
  {
    question: "Can AgentDart send emails directly?",
    answer:
      "Yes, with your permission. AgentDart offers a permission-to-send setting for direct email sending. Without that permission, it writes the email and saves it to your draft folder for you to review and send.",
  },
  {
    question: "How much does CapitalDart cost after the trial?",
    answer:
      "The paid plan costs $500 once, with no subscription. It includes 500 investor-contact credits and 5 additional analysis reports. Investor Readiness Reports are free on every deal, and Deal Update Reports are free and unlimited. Eligible teams can receive the conditional $250 rebate described above.",
  },
  {
    question: "Does CapitalDart read my email?",
    answer:
      "Only replies to outreach you sent through CapitalDart, and only when you connect Gmail or Outlook. It matches those replies to contacts on your deals, updates their status and adds a short note. It does not read other mail, attachments or full message bodies, and disconnecting stops it immediately.",
  },
  {
    question: "Do I need a paid Claude or ChatGPT subscription?",
    answer:
      "No. CapitalDart Chat is included in the web app. If you use Claude or ChatGPT, you can also connect your CapitalDart account through the available connector setup.",
    link: { to: "/connect", label: "See the setup guide." },
  },

];

/** Plain-text answer used by the FAQPage structured data. */
export function homeFaqAnswerText(item: HomeFaqItem): string {
  return item.link ? `${item.answer} ${item.link.label}` : item.answer;
}
